import React, { useState, useEffect } from 'react';
import { Table, Download, Clock, Lock, Unlock, AlertCircle, RefreshCw } from 'lucide-react';
import { useWeb3, useTokenLocker } from '../hooks/useWeb3';
import { LockTableData } from '../types/contract';
import { formatTokenAmount, formatDate, canWithdraw, getLockStatus } from '../utils/contracts';
import { ethers } from 'ethers';
import { ERC20_ABI } from '../utils/contracts';

interface LocksTableProps {
  onRefresh?: () => void;
}

export const LocksTable: React.FC<LocksTableProps> = ({ onRefresh }) => {
  const { account, isConnected, signer } = useWeb3();
  const { getUserLocks, withdraw } = useTokenLocker({ signer, isConnected });
  const [locks, setLocks] = useState<LockTableData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [withdrawingId, setWithdrawingId] = useState<bigint | null>(null);

  // Helper function to get token info
  const getTokenInfo = async (tokenAddress: string) => {
    if (!signer) return null;
    
    try {
      const contract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
      const [name, symbol, decimals] = await Promise.all([
        contract.name(),
        contract.symbol(),
        contract.decimals()
      ]);
      return { name, symbol, decimals };
    } catch (error) {
      console.error('Error fetching token info:', error);
      return null;
    }
  };

  // Fetch user locks
  const fetchLocks = async () => {
    if (!account || !isConnected) {
      setLocks([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const userLocks = await getUserLocks(account);
      const locksData: LockTableData[] = [];

      for (const lock of userLocks) {
        try {
          // Get token info
          const tokenInfo = await getTokenInfo(lock.token);
          
          const lockData: LockTableData = {
            id: lock.id,
            symbol: tokenInfo?.symbol || 'Unknown',
            tokenAddress: lock.token,
            type: getLockStatus(lock.unlockDate, lock.withdrawn),
            startDate: new Date(Number(lock.unlockDate) * 1000),
            unlockDate: new Date(Number(lock.unlockDate) * 1000),
            amountLocked: lock.amount,
            canWithdraw: canWithdraw(lock.unlockDate, lock.withdrawn)
          };

          locksData.push(lockData);
        } catch (error) {
          console.error('Error processing lock:', error);
        }
      }

      setLocks(locksData);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch locks');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLocks();
  }, [account, isConnected]);

  const handleWithdraw = async (lockId: bigint) => {
    setWithdrawingId(lockId);
    setError(null);

    try {
      await withdraw(lockId);
      await fetchLocks(); // Refresh the list
      onRefresh?.();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to withdraw');
    } finally {
      setWithdrawingId(null);
    }
  };

  const getStatusIcon = (type: string) => {
    switch (type) {
      case 'Locked':
        return <Lock className="w-4 h-4 text-yellow-400" />;
      case 'Unlocked':
        return <Unlock className="w-4 h-4 text-green-400" />;
      case 'Withdrawn':
        return <Download className="w-4 h-4 text-blue-400" />;
      default:
        return <AlertCircle className="w-4 h-4 text-zinc-400" />;
    }
  };

  const getStatusColor = (type: string) => {
    switch (type) {
      case 'Locked':
        return 'text-yellow-400';
      case 'Unlocked':
        return 'text-green-400';
      case 'Withdrawn':
        return 'text-blue-400';
      default:
        return 'text-zinc-400';
    }
  };

  if (!isConnected) {
    return (
      <div className="bg-zinc-800/50 backdrop-blur-md rounded-xl border border-zinc-600 p-6">
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <p className="text-zinc-400">Please connect your wallet to view your locks</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-800/50 backdrop-blur-md rounded-xl border border-zinc-600 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-400/20 rounded-lg">
            <Table className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h2 className="text-2xl font-zenjirou text-white">Your Locks</h2>
            <p className="text-zinc-400 text-sm">Manage your token locks</p>
          </div>
        </div>
        <button
          onClick={fetchLocks}
          disabled={isLoading}
          className="p-2 bg-zinc-700/50 border border-zinc-600 rounded-lg text-zinc-400 hover:bg-zinc-700/70 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-800 rounded-lg mb-4">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-8">
          <RefreshCw className="w-8 h-8 text-zinc-400 mx-auto mb-4 animate-spin" />
          <p className="text-zinc-400">Loading your locks...</p>
        </div>
      ) : locks.length === 0 ? (
        <div className="text-center py-8">
          <Lock className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
          <p className="text-zinc-400">No locks found</p>
          <p className="text-zinc-500 text-sm">Create your first lock to get started</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-700">
                <th className="text-left py-3 px-4 text-zinc-300 font-medium">Symbol</th>
                <th className="text-left py-3 px-4 text-zinc-300 font-medium">Address</th>
                <th className="text-left py-3 px-4 text-zinc-300 font-medium">Type</th>
                <th className="text-left py-3 px-4 text-zinc-300 font-medium">Start</th>
                <th className="text-left py-3 px-4 text-zinc-300 font-medium">Unlock Time</th>
                <th className="text-left py-3 px-4 text-zinc-300 font-medium">Amount Locked</th>
                <th className="text-left py-3 px-4 text-zinc-300 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {locks.map((lock) => (
                <tr key={lock.id.toString()} className="border-b border-zinc-700/50 hover:bg-zinc-700/20 transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-purple-400/20 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-purple-400">
                          {lock.symbol.charAt(0)}
                        </span>
                      </div>
                      <span className="text-white font-medium">{lock.symbol}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-zinc-400 text-sm font-mono">
                      {lock.tokenAddress.slice(0, 6)}...{lock.tokenAddress.slice(-4)}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(lock.type)}
                      <span className={getStatusColor(lock.type)}>{lock.type}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-zinc-400 text-sm">
                      {formatDate(BigInt(Math.floor(lock.startDate.getTime() / 1000)))}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-zinc-400" />
                      <span className="text-zinc-400 text-sm">
                        {formatDate(BigInt(Math.floor(lock.unlockDate.getTime() / 1000)))}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-white font-medium">
                      {formatTokenAmount(lock.amountLocked)} {lock.symbol}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {lock.canWithdraw ? (
                      <button
                        onClick={() => handleWithdraw(lock.id)}
                        disabled={withdrawingId === lock.id}
                        className="px-3 py-1 bg-green-400/20 border border-green-400/30 rounded text-green-400 hover:bg-green-400/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                      >
                        {withdrawingId === lock.id ? 'Withdrawing...' : 'Withdraw'}
                      </button>
                    ) : (
                      <span className="text-zinc-500 text-sm">Not available</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {locks.length > 0 && (
        <div className="mt-4 text-center">
          <p className="text-zinc-500 text-sm">
            Showing {locks.length} lock{locks.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
};
